import { useState } from "react";
import { Info, CircleDot, CheckCircle2, Zap } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

/**
 * Data Sources popover — makes it obvious which parts of the page are backed
 * by real DB / Nexthink data vs mocked-but-plausible. Great for CIO/CTO Q&A.
 *
 * `rows` shape: [{ label, source, mocked, prodSource }]
 */
export default function DataSourcesPopover({ rows, title = "Where does this data come from?", pill = "Data sources" }) {
  const [open, setOpen] = useState(false);
  const live = rows.filter((r) => !r.mocked).length;
  const mock = rows.length - live;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="data-sources-btn"
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-slate-200 hover:border-[#0033A0]/40 hover:bg-slate-50 text-slate-600 font-semibold transition-colors"
        >
          <Info size={11} className="text-[#0033A0]" />
          {pill}
          <span className="text-[9px] text-slate-400">·</span>
          <span className="text-[9px] text-[#0F9B7F] font-mono">{live} live</span>
          <span className="text-[9px] text-slate-400">·</span>
          <span className="text-[9px] text-[#F59E0B] font-mono">{mock} mock</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[520px] p-0 bg-white shadow-lg border border-slate-200"
        data-testid="data-sources-popover"
      >
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Transparency</div>
          <div className="font-display font-bold text-slate-900 text-sm mt-0.5">{title}</div>
        </div>

        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Element</th>
                <th className="text-left px-3 py-2 font-semibold">Today</th>
                <th className="text-left px-3 py-2 font-semibold">Prod source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60 align-top">
                  <td className="px-3 py-2 font-semibold text-slate-900">{r.label}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {r.mocked ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
                          <span className="text-[#B45309] font-semibold">Mock</span>
                        </>
                      ) : (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#0F9B7F]" />
                          <span className="text-[#0F9B7F] font-semibold">Live</span>
                        </>
                      )}
                    </div>
                    <div className="text-slate-500 text-[10px] mt-0.5">{r.source}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-[10px]">{r.prodSource}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#0F9B7F]" /> Live · from Mongo or a real integration</div>
          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> Mock · realistic values structured like the real thing</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
